import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { FaSearch, FaEnvelope, FaCheck, FaTimes, FaEye, FaRegEye, FaExternalLinkAlt } from 'react-icons/fa';
import { toast } from 'react-toastify';
import { 
  getCustomers, 
  sendCustomerLinks, 
  processCustomer, 
  rejectCustomer, 
  getSentLinksCount,
  getCustomerDetail,
  saveCustomerDetails,
  getCustomerPortalLink 
} from '../../../models/customersModel';
import CustomerReviewModal from './CustomerReviewModal';

function CustomersList() {
  const [customers, setCustomers] = useState([]);
  const [activeTab, setActiveTab] = useState('all');
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [totalCount, setTotalCount] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  const [sendingLinks, setSendingLinks] = useState(false);
  const [sendSettings, setSendSettings] = useState({
    method: 2, // 0: SMS only, 1: Email only, 2: Both
    gateway: 'smtp',
    batch: 20,
    test: false,
    accounts: ''
  });
  const [stats, setStats] = useState({
    incognito: 0,
    received: 0,
    processed: 0,
    sent: 0,
    total: 0
  });
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [isReviewOpen, setIsReviewOpen] = useState(false);
  const [savingReview, setSavingReview] = useState(false);
  const [processingSubmission, setProcessingSubmission] = useState(false);
  const [rejectingSubmission, setRejectingSubmission] = useState(false);
  const [portalLinkCache, setPortalLinkCache] = useState({});

  const normaliseCustomer = useCallback((record) => {
    if (!record) {
      return record;
    }

    const processedFlag = record.processed === 1 || record.processed === '1';
    const sentFlag = record.sent === 1 || record.sent === '1';
    const newDetailsProvided = Boolean(record.new_email || record.new_mobile || record.new_name || record.new_id);
    let status = record.status;

    if (status === undefined || status === null) {
      if (processedFlag) {
        status = 2;
      } else if (sentFlag && newDetailsProvided) {
        status = 1;
      } else if (!record.account_email && !record.account_mobile) {
        status = 0;
      } else if (sentFlag) {
        status = 3;
      } else {
        status = 0;
      }
    }

    const accountReference = record.account !== undefined && record.account !== null
      ? String(record.account)
      : '';

    return { ...record, account: accountReference, status: Number(status) };
  }, []);

  const resolveMessage = useCallback((response, fallback) => {
    if (response?.message && response.message !== 'Success') {
      return response.message;
    }

    if (typeof response?.data === 'string' && response.data.length > 0) {
      return response.data;
    }

    return fallback;
  }, []);

  const buildCustomerPortalUrl = useCallback((record) => {
    if (!record) {
      return null;
    }

    const candidateLink = record.portal_link || record.customer_link || record.update_link;
    if (candidateLink) {
      return candidateLink;
    }

    return null;
  }, []);

  const fetchSentLinksCount = useCallback(async () => {
    try {
      const response = await getSentLinksCount();
      if (response.status === 1) {
        setStats(prev => ({ ...prev, sent: response.data.sent || 0 }));
      }
    } catch (error) {
      console.error('Error fetching sent links count:', error);
    }
  }, []);

  const fetchCustomers = useCallback(async () => {
    try {
      setLoading(true);
      const params = {
        page: currentPage,
        pageSize: pageSize,
        status: activeTab === 'all' ? undefined : activeTab
      };
      
      if (searchTerm) params.filter = searchTerm;
      
      const response = await getCustomers(params);
      if (response.status === 1) {
        const responseData = response.data.data || [];
        const normalisedData = responseData.map(normaliseCustomer);
        const meta = response.data.meta || {};
        
        setCustomers(normalisedData);
        setTotalCount(meta.total || responseData.length);
        
        // Update stats from response
        setStats({
          incognito: response.data.incognito?.length || 0,
          received: response.data.received?.length || 0,
          processed: response.data.processed?.length || 0,
          sent: response.data.sent?.length || 0,
          total: meta.total || responseData.length
        });
      }
    } catch (error) {
      console.error('Error fetching customers:', error);
    } finally {
      setLoading(false);
    }
  }, [currentPage, pageSize, activeTab, searchTerm, normaliseCustomer]);

  useEffect(() => {
    fetchCustomers();
    fetchSentLinksCount();
  }, [fetchCustomers, fetchSentLinksCount]);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, activeTab]);

  useEffect(() => {
    const interval = setInterval(() => {
      fetchSentLinksCount();
    }, 15000);

    return () => clearInterval(interval);
  }, [fetchSentLinksCount]);

  const handleSendLinks = async () => {
    if (sendSettings.test && !sendSettings.accounts.trim()) {
      toast.warning('Please provide at least one test account before sending.');
      return;
    }

    if (!window.confirm('Send account update links using the selected settings?')) {
      return;
    }

    const payload = {
      method: sendSettings.method,
      gateway: sendSettings.gateway,
      batch: sendSettings.batch
    };

    if (sendSettings.test) {
      payload.test = true;
      payload.accounts = sendSettings.accounts;
    }

    try {
      setSendingLinks(true);
      const response = await sendCustomerLinks(payload);
      if (response.status === 1) {
        toast.success(resolveMessage(response, 'Account update links sent successfully!'));
        fetchCustomers();
        fetchSentLinksCount();
      } else {
        toast.error(response.message || 'Failed to send account update links.');
      }
    } catch (error) {
      console.error('Error sending customer links:', error);
      toast.error('Error sending account update links.');
    } finally {
      setSendingLinks(false);
    }
  };

  const handleProcessCustomer = async (account) => {
    if (!window.confirm(`Confirm acceptance of submission for account ${account}?`)) {
      return;
    }

    try {
      const response = await processCustomer(account);
      if (response.status === 1) {
        toast.success(resolveMessage(response, 'Customer processed successfully!'));
        fetchCustomers();
        fetchSentLinksCount();
      } else {
        toast.error(response.message || 'Failed to process customer.');
      }
    } catch (error) {
      console.error('Error processing customer:', error);
      toast.error('Error processing customer.');
    }
  };

  const handleRejectCustomer = async (account) => {
    if (!window.confirm(`Reject the submission for account ${account}?`)) {
      return;
    }

    try {
      const response = await rejectCustomer(account);
      if (response.status === 1) {
        toast.info(resolveMessage(response, 'Customer rejected successfully.'));
        fetchCustomers();
        fetchSentLinksCount();
      } else {
        toast.error(response.message || 'Failed to reject customer.');
      }
    } catch (error) {
      console.error('Error rejecting customer:', error);
      toast.error('Error rejecting customer.');
    }
  };

  const updateCustomerReference = useCallback((updatedCustomer) => {
    const mergedCustomer = normaliseCustomer(updatedCustomer);
    setCustomers((prev) =>
      prev.map((item) =>
        String(item.account) === String(mergedCustomer.account) ? { ...item, ...mergedCustomer } : item
      )
    );
  }, [normaliseCustomer]);

  const closeReviewModal = () => {
    setIsReviewOpen(false);
    setSelectedCustomer(null);
    setProcessingSubmission(false);
    setRejectingSubmission(false);
    setSavingReview(false);
  };

  const openReviewModal = async (customer) => {
    setSelectedCustomer(normaliseCustomer(customer));
    setIsReviewOpen(true);

    try {
      const response = await getCustomerDetail(customer.account);
      if (response.status === 1 && response.data) {
        const normalised = normaliseCustomer(response.data);
        setSelectedCustomer(normalised);
        updateCustomerReference(normalised);
      }
    } catch (error) {
      console.error('Error loading customer detail:', error);
      toast.error('Unable to load the latest customer details.');
    }
  };

  const handleSaveReview = async (formValues) => {
    if (!selectedCustomer) {
      return;
    }

    try {
      setSavingReview(true);
      const payload = {
        ...formValues,
        account: selectedCustomer.account
      };

      const response = await saveCustomerDetails(payload);
      if (response.status === 1) {
        const updatedCustomerRaw = response.data || {
          ...selectedCustomer,
          ...formValues
        };
        const updatedCustomer = normaliseCustomer(updatedCustomerRaw);
        setSelectedCustomer(updatedCustomer);
        updateCustomerReference(updatedCustomer);
        toast.success(resolveMessage(response, 'Customer details updated.'));
      } else {
        toast.error(response.message || 'Failed to save changes.');
      }
    } catch (error) {
      console.error('Error saving customer changes:', error);
      toast.error('Error saving customer changes.');
    } finally {
      setSavingReview(false);
    }
  };

  const handleConfirmSubmission = async () => {
    if (!selectedCustomer || selectedCustomer.status !== 1) {
      return;
    }

    try {
      setProcessingSubmission(true);
      const response = await processCustomer(selectedCustomer.account);
      if (response.status === 1) {
        toast.success(resolveMessage(response, 'Submission confirmed.'));
        closeReviewModal();
        fetchCustomers();
        fetchSentLinksCount();
      } else {
        toast.error(response.message || 'Failed to confirm submission.');
      }
    } catch (error) {
      console.error('Error confirming submission:', error);
      toast.error('Error confirming submission.');
    } finally {
      setProcessingSubmission(false);
    }
  };

  const handleRejectSubmission = async () => {
    if (!selectedCustomer || selectedCustomer.status !== 1) {
      return;
    }

    try {
      setRejectingSubmission(true);
      const response = await rejectCustomer(selectedCustomer.account);
      if (response.status === 1) {
        toast.info(resolveMessage(response, 'Submission rejected.'));
        closeReviewModal();
        fetchCustomers();
        fetchSentLinksCount();
      } else {
        toast.error(response.message || 'Failed to reject submission.');
      }
    } catch (error) {
      console.error('Error rejecting submission:', error);
      toast.error('Error rejecting submission.');
    } finally {
      setRejectingSubmission(false);
    }
  };

  const totalPages = Math.ceil(totalCount / pageSize);

  const handlePageSizeChange = (e) => {
    setPageSize(Number(e.target.value));
    setCurrentPage(1);
  };

  const sentPercentage = useMemo(() => {
    const total = stats.total || totalCount;
    if (!total) {
      return 0;
    }

    const calculated = Math.round((stats.sent / total) * 100);
    return Math.min(100, Math.max(0, calculated));
  }, [stats.sent, stats.total, totalCount]);

  const openCustomerPortal = useCallback(async (record) => {
    if (!record) {
      toast.warn('Customer view link unavailable for this record.');
      return;
    }

    const cacheKey = record.account;
    const cachedLink = cacheKey ? portalLinkCache[cacheKey] : null;
    let portalUrl = buildCustomerPortalUrl(record) || cachedLink;

    if (!portalUrl && cacheKey) {
      try {
        const response = await getCustomerPortalLink(cacheKey);
        if (response.status === 1 && response.data?.link) {
          portalUrl = response.data.link;
          setPortalLinkCache(prev => ({ ...prev, [cacheKey]: portalUrl }));
        } else {
          toast.warn(resolveMessage(response, 'Customer view link unavailable for this record.'));
          return;
        }
      } catch (error) {
        console.error('Error fetching customer portal link:', error);
        toast.error('Unable to open customer view.');
        return;
      }
    }

    if (!portalUrl) {
      toast.warn('Customer view link unavailable for this record.');
      return;
    }

    const newWindow = window.open(portalUrl, '_blank', 'noopener,noreferrer');
    if (newWindow) {
      newWindow.opener = null;
    }
  }, [buildCustomerPortalUrl, portalLinkCache, resolveMessage]);



  if (loading && customers.length === 0) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-2 py-1">
      {/* Header Card */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-4">
        <div className="flex items-start justify-between">
          <div className="flex items-start">
            <div className="flex-shrink-0 rounded-lg p-2.5 mr-3 mt-0.5" style={{ backgroundColor: 'rgba(0, 135, 212, 0.15)' }}>
              <FaRegEye className="h-5 w-5" style={{ color: '#0096DE' }} />
            </div>
            <div>
              <h1 className="text-xl font-bold text-gray-800">Customer Management</h1>
              <p className="text-xs text-gray-500 mb-1">
                Customer Account Management • 
                <span className="text-blue-600">Incognito: {stats.incognito}</span> • 
                <span className="text-green-600">Received: {stats.received}</span> • 
                <span className="text-yellow-600">Processed: {stats.processed}</span> • 
                <span className="text-purple-600">Sent Links: {stats.sent}</span>
              </p>
              <div className="flex items-center mt-1">
                <span className="text-xs font-medium" style={{ color: '#0096DE' }}>
                  {totalCount ? `Showing ${Math.min(pageSize, totalCount)} of ${totalCount} results` : '0 results'}
                  {(searchTerm || activeTab !== 'all') && (
                    <span className="ml-1 text-blue-600">(filtered)</span>
                  )}
                </span>
              </div>
            </div>
          </div>
          
          <div className="flex flex-col gap-2 items-end min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              {(searchTerm || activeTab !== 'all') && (
                <button
                  onClick={() => {
                    setSearchTerm('');
                    setActiveTab('all');
                  }}
                  className="inline-flex items-center px-2 py-1.5 border border-red-300 rounded-md shadow-sm text-xs text-red-700 bg-red-50 hover:bg-red-100 focus:outline-none focus:ring-1 focus:ring-red-500 flex-shrink-0"
                >
                  Clear Filters
                </button>
              )}
              
              <button 
                onClick={() => fetchCustomers()} 
                className="inline-flex items-center px-2 py-1.5 border border-gray-300 shadow-sm text-sm rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-1 focus:ring-blue-500 flex-shrink-0"
                title="Refresh"
                disabled={loading}
              >
                <svg xmlns="http://www.w3.org/2000/svg" className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} fill="none" viewBox="0 0 24 24" stroke={loading ? '#0096DE' : 'currentColor'}>
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
              </button>
              
              <div className="flex-shrink-0">
                <select
                  value={activeTab}
                  onChange={(e) => setActiveTab(e.target.value)}
                  className={`text-sm px-2 py-1.5 border rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 ${
                    activeTab !== 'all' ? 'border-blue-300 bg-blue-50 text-blue-700' : 'border-gray-300 bg-white'
                  }`}
                >
                  <option value="all">All Customers</option>
                  <option value="incognito">Incognito ({stats.incognito})</option>
                  <option value="received">Received ({stats.received})</option>
                  <option value="processed">Processed ({stats.processed})</option>
                  <option value="sent">Sent Links ({stats.sent})</option>
                </select>
              </div>

              <div className="flex-shrink-0">
                <select
                  value={pageSize}
                  onChange={handlePageSizeChange}
                  className="text-sm px-2 py-1.5 border border-gray-300 bg-white rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                >
                  {[10, 20, 30, 50].map(size => (
                    <option key={size} value={size}>{size} per page</option>
                  ))}
                </select>
              </div>
            </div>
            
            <div className="relative w-full">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <FaSearch className="h-4 w-4 text-gray-400" />
              </div>
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                placeholder="Search customers..."
              />
            </div>
          </div>
        </div>

        <div className="mt-6 pt-4 border-t border-gray-100">
          <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mb-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Method</label>
              <select
                value={sendSettings.method}
                onChange={(e) => setSendSettings(prev => ({ ...prev, method: parseInt(e.target.value) }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
              >
                <option value={0}>SMS Only</option>
                <option value={1}>Email Only</option>
                <option value={2}>Both SMS & Email</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Batch Size</label>
              <select
                value={sendSettings.batch}
                onChange={(e) => setSendSettings(prev => ({ ...prev, batch: parseInt(e.target.value, 10) }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
              >
                {[20, 50, 100, 200, 500, 1000, 5000].map((size) => (
                  <option key={size} value={size}>{size}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Gateway</label>
              <select
                value={sendSettings.gateway}
                onChange={(e) => setSendSettings(prev => ({ ...prev, gateway: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
              >
                <option value="smtp">SMTP</option>
                <option value="mail">Mail</option>
              </select>
            </div>
            <div className="flex items-end">
              <label className="flex items-center text-sm font-medium text-gray-700">
                <input
                  type="checkbox"
                  checked={sendSettings.test}
                  onChange={(e) => setSendSettings(prev => ({ ...prev, test: e.target.checked }))}
                  className="mr-2"
                />
                Test Mode
              </label>
            </div>
            <div className="flex items-end">
              <button
                onClick={handleSendLinks}
                disabled={sendingLinks}
                className={`w-full px-4 py-2 rounded-md text-white flex items-center justify-center ${
                  sendingLinks
                    ? 'bg-gray-400 cursor-not-allowed'
                    : 'bg-blue-500 hover:bg-blue-600'
                }`}
              >
                {sendingLinks ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Sending...
                  </>
                ) : (
                  <>
                    <FaEnvelope className="mr-2" />
                    Send Links
                  </>
                )}
              </button>
            </div>
          </div>

          <div className="bg-blue-50 border border-blue-100 rounded-md p-3 flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
            <div className="text-sm font-medium text-blue-900">Sent Progress</div>
            <div className="flex-1 flex items-center gap-3">
              <div className="flex-1 h-2 bg-blue-100 rounded-full overflow-hidden">
                <div
                  className="h-full bg-blue-500"
                  style={{ width: `${sentPercentage}%` }}
                ></div>
              </div>
              <span className="text-sm font-semibold text-blue-800 w-16 text-right">{sentPercentage}%</span>
              <span className="text-xs text-blue-700">
                {stats.sent} / {stats.total || totalCount || 0}
              </span>
            </div>
          </div>

          {sendSettings.test && (
            <div className="mt-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">Test Accounts</label>
              <textarea
                value={sendSettings.accounts}
                onChange={(e) => setSendSettings(prev => ({ ...prev, accounts: e.target.value }))}
                placeholder="Enter account numbers (one per line)"
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
              />
            </div>
          )}
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200">

        <div className="overflow-x-auto">
          <table className="w-full divide-y divide-gray-200">
            <thead style={{ backgroundColor: 'rgba(0, 135, 212, 0.08)' }}>
              <tr>
                <th className="px-3 py-2 text-left text-xs font-semibold uppercase" style={{ color: '#0087D4' }}>Account</th>
                <th className="px-3 py-2 text-left text-xs font-semibold uppercase" style={{ color: '#0087D4' }}>Name</th>
                <th className="px-3 py-2 text-left text-xs font-semibold uppercase" style={{ color: '#0087D4' }}>Email</th>
                <th className="px-3 py-2 text-left text-xs font-semibold uppercase" style={{ color: '#0087D4' }}>Mobile</th>
                <th className="px-3 py-2 text-left text-xs font-semibold uppercase" style={{ color: '#0087D4' }}>Status</th>
                <th className="px-3 py-2 text-left text-xs font-semibold uppercase" style={{ color: '#0087D4' }}>Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {loading && (
                <tr>
                  <td colSpan="6" className="px-3 py-4 text-center">
                    <div className="flex items-center justify-center space-x-2">
                      <svg className="animate-spin h-4 w-4 text-blue-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      <span className="text-sm">Loading...</span>
                    </div>
                  </td>
                </tr>
              )}
              {!loading && customers.length === 0 && (
                <tr>
                  <td colSpan="6" className="px-3 py-6 text-center">
                    <p className="text-gray-600">No customers found</p>
                  </td>
                </tr>
              )}
              {!loading && customers.map((customer) => (
                <tr key={customer.account || customer.id} className="hover:bg-blue-50">
                  <td className="px-3 py-2 text-sm font-medium text-gray-900">
                    {customer.account}
                  </td>
                  <td className="px-3 py-2 text-sm text-gray-900">
                    {customer.account_name}
                  </td>
                  <td className="px-3 py-2 text-sm text-gray-900">
                    {customer.account_email || '-'}
                  </td>
                  <td className="px-3 py-2 text-sm text-gray-900">
                    {customer.account_mobile || '-'}
                  </td>
                  <td className="px-3 py-2 text-sm">
                    <span className={`inline-block px-3 py-1 rounded font-semibold text-xs ${
                      customer.status === 0 ? 'bg-gray-100 text-gray-800' :
                      customer.status === 1 ? 'bg-blue-100 text-blue-800' :
                      customer.status === 2 ? 'bg-green-100 text-green-800' :
                      customer.status === 3 ? 'bg-purple-100 text-purple-800' :
                      'bg-gray-100 text-gray-600'
                    }`}>
                      {customer.status === 0 ? 'Incognito' :
                       customer.status === 1 ? 'Received' :
                       customer.status === 2 ? 'Processed' :
                       customer.status === 3 ? 'Sent' : 'Unknown'}
                    </span>
                  </td>
                  <td className="px-3 py-2 text-sm">
                    <div className="flex space-x-2">
                      <button
                        onClick={() => openCustomerPortal(customer)}
                        className="text-indigo-600 hover:text-indigo-900"
                        title="Open Customer View"
                      >
                        <FaExternalLinkAlt />
                      </button>
                      <button
                        onClick={() => openReviewModal(customer)}
                        className="text-blue-600 hover:text-blue-900"
                        title="Review Submission"
                      >
                        <FaEye />
                      </button>
                      {customer.status === 1 && (
                        <>
                          <button
                            onClick={() => handleProcessCustomer(customer.account)}
                            className="text-green-600 hover:text-green-900"
                            title="Confirm Submission"
                          >
                            <FaCheck />
                          </button>
                          <button
                            onClick={() => handleRejectCustomer(customer.account)}
                            className="text-red-600 hover:text-red-900"
                            title="Reject Submission"
                          >
                            <FaTimes />
                          </button>
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        
        {/* Pagination */}
        {totalCount > 0 && (
          <div className="bg-gray-50 px-3 py-3 border-t border-gray-200 flex flex-col sm:flex-row items-center justify-between gap-3 text-xs">
            <div className="flex items-center gap-4">
              <div className="text-gray-500">
                Showing {((currentPage - 1) * pageSize) + 1} to {Math.min(currentPage * pageSize, totalCount)} of {totalCount} results
              </div>
              
              <div className="flex items-center gap-2">
                <span className="text-gray-500">Show:</span>
                <select
                  value={pageSize}
                  onChange={(e) => {
                    setPageSize(Number(e.target.value));
                    setCurrentPage(1);
                  }}
                  className="border border-gray-300 rounded px-2 py-1 text-xs bg-white focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                >
                  {[10, 20, 50, 100].map(size => (
                    <option key={size} value={size}>{size}</option>
                  ))}
                </select>
                <span className="text-gray-500">per page</span>
              </div>
            </div>
            
            <div className="flex items-center space-x-1">
              <button
                onClick={() => setCurrentPage(1)}
                disabled={currentPage === 1}
                className="px-3 py-1.5 border border-gray-300 bg-white text-gray-500 hover:bg-gray-50 disabled:opacity-40 rounded text-xs font-medium"
              >
                First
              </button>
              <button
                onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                disabled={currentPage === 1}
                className="px-3 py-1.5 border border-gray-300 bg-white text-gray-500 hover:bg-gray-50 disabled:opacity-40 rounded text-xs font-medium"
              >
                Previous
              </button>
              
              {/* Page Numbers */}
              <div className="flex items-center space-x-1">
                {(() => {
                  const pageNumbers = [];
                  let startPage = Math.max(1, currentPage - 2);
                  let endPage = Math.min(totalPages, startPage + 4);
                  
                  if (endPage - startPage < 4) {
                    startPage = Math.max(1, endPage - 4);
                  }
                  
                  for (let i = startPage; i <= endPage; i++) {
                    pageNumbers.push(
                      <button
                        key={i}
                        onClick={() => setCurrentPage(i)}
                        className={`px-3 py-1.5 border rounded text-xs font-medium ${
                          i === currentPage
                            ? 'bg-blue-600 text-white border-blue-600'
                            : 'bg-white text-gray-500 border-gray-300 hover:bg-gray-50'
                        }`}
                      >
                        {i}
                      </button>
                    );
                  }
                  
                  return pageNumbers;
                })()}
              </div>
              
              <button
                onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                disabled={currentPage === totalPages}
                className="px-3 py-1.5 border border-gray-300 bg-white text-gray-500 hover:bg-gray-50 disabled:opacity-40 rounded text-xs font-medium"
              >
                Next
              </button>
              <button
                onClick={() => setCurrentPage(totalPages)}
                disabled={currentPage === totalPages}
                className="px-3 py-1.5 border border-gray-300 bg-white text-gray-500 hover:bg-gray-50 disabled:opacity-40 rounded text-xs font-medium"
              >
                Last
              </button>
            </div>
          </div>
        )}
      </div>
      <CustomerReviewModal
        isOpen={isReviewOpen}
        customer={selectedCustomer}
        onClose={closeReviewModal}
        onSave={handleSaveReview}
        onConfirm={handleConfirmSubmission}
        onReject={handleRejectSubmission}
        saving={savingReview}
        processing={processingSubmission}
        rejecting={rejectingSubmission}
        canDecide={selectedCustomer?.status === 1}
      />
    </div>
  );
}

export default CustomersList;
